// =====================================
// ENUMS (from server models)
// =====================================

export enum Permission {
  VIEW_STUDENTS = 'view_students',
  MANAGE_STUDENTS = 'manage_students',
  VIEW_GOALS = 'view_goals',
  MANAGE_GOALS = 'manage_goals',
  VIEW_ACCOMMODATIONS = 'view_accommodations',
  LOG_ACCOMMODATIONS = 'log_accommodations',
  MANAGE_ACCOMMODATIONS = 'manage_accommodations',
  VIEW_REPORTS = 'view_reports',
  EXPORT_REPORTS = 'export_reports',
  MANAGE_USERS = 'manage_users',
  MANAGE_CLASSES = 'manage_classes',
  MANAGE_SCHOOLS = 'manage_schools',
  MANAGE_ORGANIZATION = 'manage_organization',
}

export enum ResourceScope {
  OWN = 'own',
  CLASS = 'class',
  SCHOOL = 'school',
  ORGANIZATION = 'organization',
}

// Grade enum from Student model
export enum Grade {
  PRE_K = 'Pre-K',
  K = 'K',
  GRADE_1 = 'Grade 1',
  GRADE_2 = 'Grade 2',
  GRADE_3 = 'Grade 3',
  GRADE_4 = 'Grade 4',
  GRADE_5 = 'Grade 5',
  GRADE_6 = 'Grade 6',
  GRADE_7 = 'Grade 7',
  GRADE_8 = 'Grade 8',
  GRADE_9 = 'Grade 9',
  GRADE_10 = 'Grade 10',
  GRADE_11 = 'Grade 11',
  GRADE_12 = 'Grade 12',
}

// Race enum from Student model
export enum Race {
  AMERICAN_INDIAN_ALASKA_NATIVE = 'American Indian or Alaska Native',
  ASIAN = 'Asian',
  BLACK_AFRICAN_AMERICAN = 'Black or African American',
  HISPANIC_LATINO = 'Hispanic',
  NATIVE_HAWAIIAN_PACIFIC_ISLANDER = 'Native Hawaiian or Other Pacific Islander',
  WHITE = 'White',
  MULTIPLE_RACES = 'Multiple Races',
}

// Gender enum from Student model
export enum Gender {
  MALE = 'Male',
  FEMALE = 'Female',
}

// Accommodation enum from AccommodationEvent model
export enum Accommodation {
  // Presentation Accommodations
  ANSWER_MASKING = '1a: Answer Masking',
  AUDIO_AMPLIFICATION = '1b: Audio Amplification',
  BOOKMARK_FLAG_ITEM = '1c: Bookmark (flag item for review)',
  COLOR_CONTRAST = '1d: Color Contrast (Background/Font Color)',
  BLANK_SCRATCH_PAPER = '1e: Blank Scratch Paper',
  ELIMINATE_ANSWER_CHOICES = '1f: Eliminate Answer Choices',
  GENERAL_DIRECTIONS_CLARIFIED = '1g: General Directions Clarified',
  GENERAL_DIRECTIONS_READ_ALOUD = '1h: General Directions Read Aloud and Repeated as Needed',
  HIGHLIGHT_TOOL = '1i: Highlight Tool',
  HEADPHONES_OR_NOISE_BUFFERS = '1j: Headphones or Noise Buffers',
  LINE_READER_MASK_TOOL = '1k: Line Reader Mask Tool',
  MAGNIFICATION_ENLARGEMENT_DEVICE = '1l: Magnification/Enlargement Device',
  NOTEPAD = '1m: Notepad',
  POP_UP_GLOSSARY = '1n: Pop-up Glossary',
  REDIRECT_STUDENT = '1o: Redirect Student',
  SPELL_CHECK_OR_EXTERNAL_SPELL_CHECK = '1p: Spell Check or External Spell Check Device',
  STUDENT_READS_CONTENT_ALOUD = '1q: Student Reads Content Aloud to Him or Herself',
  TEXT_TO_SPEECH_MATH_SCI_SOC = '1r: Text-to-Speech for the Mathematics, Science and Social Studies Assessments (Available in English only)',
  HUMAN_READER_MATH_SCI_SOC = '1s: Human Reader or Human Signer for the Mathematics, Science and Social Studies Assessments (Human Reader available in English and in Spanish)',
  WRITING_TOOLS = '1t: Writing Tools',
  GRAPHIC_ORGANIZER = '1u: Graphic Organizer',
  AUDIO_MATERIALS = '1v: Audio Materials',
  SPANISH_VERSION_COMPUTER_ASSESSMENT = '1w: Spanish Version Computer based assessment',
  BLUE_TOOTH_HEARING_AIDS = '1x: Blue Tooth Hearing Aids',
  ELECTRONIC_DEVICE_MEDICAL_PURPOSES = '1y: Electronic Device for Medical Purposes',

  // Setting Accommodations
  SMALL_GROUP = '2a: Small Group',
  TIME_OF_DAY = '2b: Time of Day',
  SEPARATE_OR_ALTERNATE_LOCATION = '2c: Separate or Alternate Location',
  SPECIFIED_AREA_OR_SETTING = '2d: Specified Area or Setting',
  ADAPTIVE_OR_SPECIALIZED_EQUIPMENT = '2e: Adaptive or Specialized Equipment or Furniture',
  FREQUENT_BREAKS = '2f: Frequent Breaks',
  REDUCE_DISTRACTION_TO_SELF = '2g: Reduce Distraction to Self',
  REDUCE_DISTRACTION_TO_OTHERS = '2h: Reduce Distraction to Others',
  UNIQUE_ADMINISTRATIVE_ACCOMMODATIONS = '2k: Unique Administrative Accommodations',

  // Accessibility Features
  ASSISTIVE_TECHNOLOGY_NON_SCREEN_READER = '3a: Assistive Technology (Non-Screen Reader) (also a response accommodation)',
  SCREEN_READER_VERSION = '3b: Screen Reader Version',
  REFRESHABLE_BRAILLE_DISPLAY = '3c: Refreshable Braille Display',
  HARD_COPY_BRAILLE_EDITION = '3d: Hard Copy Braille Edition',
  TACTILE_GRAPHICS = '3e: Tactile Graphics',
  LARGE_PRINT_EDITION = '3f: Large Print Edition (Available in English or Spanish)',
  PAPER_BASED_EDITION = '3g: Paper Based Edition (Paper Test) (Available in English and in Spanish)',
  CLOSED_CAPTIONING_MULTI_MEDIA = '3h: Closed Captioning of Multi-Media Passages',
  TEXT_TO_SPEECH_ELA_LITERACY = '3i: Text-to-speech for ELA/Literacy Assessments',
  ASL_VIDEO_FOR_ELA = '3j: ASL Video for ELA',
  HUMAN_READER_SIGNER_ELA = '3k: Human Reader/Human Signer for ELA',
  ASL_VIDEO_MATH_SCI_SOC = '3l: American Sign Language (ASL) Video for Mathematics, Science, and Social Studies',
  HUMAN_SIGNER_TEST_DIRECTIONS = '3m: Human Signer for Test Directions',
  MANUAL_CONTROL_OF_AUDIO = '3n: Manual Control of Audio',
  REPEAT_ITEM_AUDIO = '3o: Repeat Item Audio',
  NOTES_AND_OUTLINES = '3p: Notes and Outlines',
  UNIQUE_PRESENTATION_ACCOMMODATIONS = '3q: Unique Presentation Accommodations',

  // Response Accommodations
  ASSISTIVE_TECHNOLOGY = '4a: Assistive Technology',
  BRAILLE_NOTETAKER = '4b: Braille Notetaker',
  BRAILLE_WRITER = '4c: Braille Writer',
  MATH_TOOLS_CALC_SECTIONS = '4d: Mathematics Tools including Calculator (on Calculator sections of the Mathematics Assessments and Science Assessments)',
  MATH_TOOLS_NON_CALC_SECTIONS = '4e: Mathematics Tools including Calculator (on non-Calculator sections of the mathematics assessments)',
  SPEECH_TO_TEXT = '4f: Speech-to-Text',
  HUMAN_SCRIBE_RESPONSE = '4g: Human Scribe Response',
  HUMAN_SIGNER_RESPONSE = '4h: Human Signer Response',
  MONITOR_TEST_RESPONSE = '4i: Monitor Test Response',
  WORD_PREDICTION_EXTERNAL_DEVICE = '4j: Word Prediction- External Device',
  ANSWERS_RECORDED_IN_TEST_BOOK = '4k: Answers Recorded in Test Book (for students taking a paper assessment)',
  RECORDING_DEVICES = '4l: Recording Devices',
  ACCESS_FOR_ELLS_SCRIBE = '4m: ACCESS for ELLs Scribe',
  PARTNER_ASSISTED_SCANNING = '4n: Partner Assisted Scanning',
  UNIQUE_RESPONSE_ACCOMMODATIONS = '4o: Unique Response Accommodations',

  // Timing and Scheduling Accommodations
  EXTENDED_TIME = '5a: Extended Time',
  UNIQUE_TIMING_AND_SCHEDULING = '5b: Unique Timing and Scheduling Accommodations',
}

export enum AccommodationStatus {
  N_A = 'N/A',
  NOT_NEEDED = 'Not Needed',
  DECLINED = 'Declined',
  PROVIDED = 'Provided',
}

// =====================================
// INTERFACES
// =====================================

export interface OrganizationRole {
  _id: string
  name: string
  description?: string
  scope: ResourceScope
  permissions: Permission[]
  templateKey?: string
  isCustom: boolean
}

export interface User {
  id?: string
  _id: string
  email: string
  firstName: string
  lastName: string
  schools: string[] | Array<{ _id: string; name: string }>
  organization:
    | string
    | {
        _id: string
        name: string
      }
  role: string | OrganizationRole
  permissions: Permission[]
  createdAt: string
  updatedAt: string
}

export interface Organization {
  _id: string
  name: string
  createdAt: string
  updatedAt: string
}

export interface School {
  _id: string
  displayName: string
  importNames?: string[]
  organization: string | Organization
  createdAt: string
  updatedAt: string
}

export interface Class {
  _id: string
  name: string
  students: string[] | Student[]
  teachers:
    | string[]
    | Array<{ _id: string; firstName: string; lastName: string; email: string }>
  school: string | School
  organization: string | Organization
  createdBy: string | User
  createdAt: string
  updatedAt: string
}

export interface Student {
  _id: string
  studentId?: string
  firstName: string
  lastName: string
  grade: Grade
  race?: Race
  gender?: Gender
  caseManager?: string
  accommodations: Accommodation[]
  school: string | School
  organization: string | Organization
  createdBy: string | User
  createdAt: string
  updatedAt: string
}

export interface AccommodationEvent {
  _id: string
  student: string | Student
  class: string | Class
  accommodation: Accommodation
  status: AccommodationStatus
  date: string
  notes?: string
  createdBy: string | User
  createdAt: string
  updatedAt: string
}

export interface IEP {
  accommodations: string[]
  services: string[]
  sas: string[]
}

export interface AuthResponse {
  user: User
  token: string
  requires2FA?: boolean
}

export interface LoginRequest {
  email: string
  password: string
}

export interface RegisterRequest {
  email: string
  password: string
  firstName: string
  lastName: string
}

export interface VerifyEmailRequest {
  token: string
}

export interface ResendVerificationRequest {
  email: string
}

export interface ForgotPasswordRequest {
  email: string
}

export interface ResetPasswordRequest {
  token: string
  password: string
}

export interface PasswordResetResponse {
  success: boolean
  message: string
}

export interface Verify2FARequest {
  email: string
  code: string
}

export interface Verify2FAResponse {
  success: boolean
  token?: string
  user?: User
  message?: string
}

export interface VerifyEmailResponse {
  success: boolean
  message: string
}

export interface CreateStudentRequest {
  studentId?: string
  firstName: string
  lastName: string
  grade: Grade
  race?: Race
  gender?: Gender
  caseManager?: string
  accommodations: Accommodation[]
  school: string
  organization: string
}

export interface UpdateStudentRequest extends Partial<CreateStudentRequest> {}

export interface CreateUserRequest {
  email: string
  password: string
  firstName: string
  lastName: string
  organization: string
  schools: string[]
  role: string
}

export interface CreateSchoolRequest {
  name: string
  organization: string
}

export interface UpdateSchoolRequest extends Partial<CreateSchoolRequest> {}

export interface CreateClassRequest {
  name: string
  students: string[]
  teachers: string[]
  school: string
  organization: string
}

export interface UpdateClassRequest extends Partial<CreateClassRequest> {}

export interface CreateAccommodationEventRequest {
  student: string
  class: string
  accommodation: Accommodation
  status: AccommodationStatus
  date: string
}

export interface CreateAccommodationEventsRequest {
  studentId: string
  classId: string
  accommodations: {
    accommodation: Accommodation
    status: AccommodationStatus
  }[]
  date: string
}

export interface UpdateAccommodationEventRequest {
  status?: AccommodationStatus
  notes?: string
}

// Goal enums from Goal model
export enum GoalCategory {
  EARLY_LEARNING_SKILLS = 'Early Learning Skills',
  ACADEMIC = 'Academic',
  SOCIAL_EMOTIONAL_BEHAVIORAL = 'Social Emotional/Behavioral',
  HEALTH = 'Health',
  PHYSICAL = 'Physical',
}

export enum GoalArea {
  // Early Learning Skills
  LANGUAGE_AND_LITERACY = 'Language and Literacy',
  EARLY_COGNITION_STEAM = 'Early Cognition and STEAM',
  SOCIAL_EMOTIONAL_DEVELOPMENT = 'Social-Emotional Development',
  PHYSICAL_MOTOR_DEVELOPMENT = 'Physical Well-being and Motor Development',
  APPROACHES_TO_LEARNING = 'Approaches to Learning',

  // Academic
  READING_PHONEMIC_AWARENESS = 'Reading Phonemic Awareness',
  READING_PHONICS = 'Reading Phonics',
  READING_FLUENCY = 'Reading Fluency',
  READING_VOCABULARY = 'Reading Vocabulary',
  READING_COMPREHENSION = 'Reading Comprehension',
  MATH_CALCULATION = 'Math Calculation',
  MATH_PROBLEM_SOLVING = 'Math Problem Solving',
  WRITTEN_LANGUAGE_CONTENT = 'Written Language Content',
  WRITTEN_LANGUAGE_MECHANICS = 'Written Language Mechanics',
  WRITTEN_LANGUAGE_EXPRESSION = 'Written Language Expression',
  SPEECH_LANGUAGE_ARTICULATION = 'Speech and Language Articulation',
  SPEECH_LANGUAGE_VOICE = 'Speech and Language Voice',
  SPEECH_LANGUAGE_RECEPTIVE = 'Speech and Language Receptive Language',
  SPEECH_LANGUAGE_EXPRESSIVE = 'Speech and Language Expressive Language',
  SPEECH_LANGUAGE_PRAGMATICS = 'Speech and Language Pragmatics',
  COMMUNICATION = 'Communication',
  COGNITIVE = 'Cognitive',
  PHYSICAL_EDUCATION = 'Physical Education',

  // Social Emotional/Behavioral
  SOCIAL_EMOTIONAL_BEHAVIORAL = 'Social Emotional/Behavioral',
  SOCIAL_INTERACTION_SKILLS = 'Social Interaction Skills',
  SELF_MANAGEMENT = 'Self-Management',

  // Health
  HEALTH_MEDICAL = 'Health/Medical',
  HEARING = 'Hearing',
  VISION = 'Vision',
  ORIENTATION_MOBILITY = 'Orientation and Mobility',

  // Physical
  FINE_MOTOR = 'Fine Motor',
  VISUAL_MOTOR = 'Visual Motor',
  HAND_SKILLS = 'Functional Upper Extremity/Hand Skills',
  FINE_MOTOR_COORDINATION = 'Fine Motor Coordination',
  MANUAL_DEXTERITY = 'Manual Dexterity',
  BIMANUAL_SKILLS = 'Bimanual Skills/Bilateral (Two-handed) Coordination',
  SENSORY_PROCESSING_STRATEGIES = 'Sensory Processing Strategies',
  GROSS_MOTOR = 'Gross Motor',
  FUNCTIONAL_MOBILITY = 'Functional Mobility',
  POSTURAL_CONTROL = 'Postural Control',
  POSITIONING = 'Positioning',
  COMMUNITY_ACCESS = 'Community Access',
  INDEPENDENT_LIVING_FEEDING = 'Independent Living - Feeding',
  INDEPENDENT_COMMUNITY_LIVING_REC = 'Independent Community Living - Rec and Leisure',
  INDEPENDENT_COMMUNITY_LIVING_TOILETING = 'Independent Community Living - Toileting',
  INDEPENDENT_COMMUNITY_LIVING_DRESSING = 'Independent Community Living - Dressing and Grooming',

  // Other
  OTHER = 'Other',
}

export interface Goal {
  _id: string
  description: string
  category: GoalCategory
  area: GoalArea
  objectives: string[]
  student: string | Student
  createdAt: string
  updatedAt: string
}

export interface CreateGoalRequest {
  description: string
  category: GoalCategory
  area: GoalArea
  objectives: string[]
  student: string
}

export interface UpdateGoalRequest extends Partial<CreateGoalRequest> {}
